<template>
  <div class="hero-image">
    <div class="heading-container uk-align-center">
      <div class="uk-text-center" id="welcome">
        <h1>{{$t('homeSearchBar.freeClassroomResources')}}</h1>
        <h2>{{$t('homeSearchBar.getActivities')}}</h2>
      </div>
    </div>
    <div id="search-container" class="search-container uk-align-center uk-visible@m">
      <div class="uk-width-1-1" uk-grid>
        <div class="uk-width-1-2">
          <div
            @mouseover="resolveHover(true)"
            @mouseleave="resolveHover(false)"
            class="search-input"
            :class="{'input-inactive': !inputActive, 'input-active': inputActive}"
          >
            <form
              v-on:submit.prevent="search"
              novalidate
              class="uk-search uk-width-1-1 uk-height-1-1"
            >
              <img
                v-if="!inputActive"
                :data-src="slateSearchIcon"
                class="icon-dimensions uk-position-center-left"
                uk-img
              />
              <router-link
                v-else
                :to="{name: 'Search', query:{searchText, key: new Date().getTime()}}"
              >
                <img
                  :data-src="blueSearchIcon"
                  class="icon-dimensions uk-position-center-left"
                  uk-img
                />
              </router-link>
              <input
                autocomplete="off"
                required
                class="uk-search-input input-search-term uk-position-center-right"
                type="search"
                :placeholder="$t('general.searchForResources')"
                v-model="searchText"
                id="home-search"
                @focus="resolveFocus(true)"
                @blur="resolveFocus(false)"
              />
            </form>
          </div>
        </div>
        <div class="uk-width-1-2">
          <button
            id="btn_grades"
            class="uk-float-left uk-button btn-dropdown"
            type="button"
            tabindex="-1"
          >
            <span v-if="highlightGrades">
              <span class="filter-text-highlight uk-text-capitalize">{{selectedGrades}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-up; ratio: 1"></span>
            </span>
            <span v-else>
              <span class="filter-text uk-text-capitalize">{{selectedGrades}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-down; ratio: 1"></span>
            </span>
          </button>

          <div
            uk-dropdown="mode: click"
            class="select-filters-container overlay"
            @hidden="highlightGrades = false"
            @show="highlightGrades = true"
          >
            <div>
              <div v-for="filter in filterGroup(GRADES_TITLE)">
                <div
                  class="select-filters"
                  @click="handleFilterUpdate(filters, filter.id, GRADES_TITLE)"
                >
                  <span v-if="filter.selected">
                    <span class="uk-text-middle add-pointer">
                      <img :data-src="LRG_CHECKED_BOX" class="lrg-checkbox" uk-img />
                    </span>
                    <span class="filter-options add-pointer uk-text-bold">{{filter.title}}</span>
                  </span>
                  <span v-else>
                    <span class="uk-text-middle add-pointer">
                      <img :data-src="LRG_UNCHECKED_BOX" class="lrg-checkbox" uk-img />
                    </span>
                    <span class="filter-options add-pointer">{{filter.title}}</span>
                  </span>
                  <span v-if="filter.id === ALL_GRADES_ID">
                    <div class="separator"></div>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button id="btn_subjects" class="uk-button btn-dropdown" type="button" tabindex="-1">
            <span v-if="highlightSubjects">
              <span class="filter-text-highlight">{{selectedSubjects}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-up; ratio: 1"></span>
            </span>
            <span v-else>
              <span class="filter-text">{{selectedSubjects}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-down; ratio: 1"></span>
            </span>
          </button>
          <div
            uk-dropdown="mode: click"
            class="select-filters-container overlay"
            @hidden="highlightSubjects = false"
            @show="highlightSubjects = true"
          >
            <div>
              <div v-for="filter in filterGroup(SUBJECTS_TITLE)">
                <div
                  class="select-filters"
                  @click="handleFilterUpdate(filters, filter.id, SUBJECTS_TITLE)"
                >
                  <span v-if="filter.selected">
                    <span class="uk-text-middle add-pointer">
                      <img :data-src="LRG_CHECKED_BOX" class="lrg-checkbox" uk-img />
                    </span>
                    <span class="filter-options add-pointer uk-text-bold">{{filter.title}}</span>
                  </span>
                  <span v-else>
                    <span class="uk-text-middle add-pointer">
                      <img :data-src="LRG_UNCHECKED_BOX" class="lrg-checkbox" uk-img />
                    </span>
                    <span class="filter-options add-pointer">{{filter.title}}</span>
                  </span>
                  <span v-if="filter.id === ALL_SUBJECTS_ID">
                    <div class="separator"></div>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <router-link :to="{name: 'Search', query:{searchText, key: new Date().getTime()}}">
            <button
              id="search-btn"
              class="uk-button btn-search uk-float-right uk-text-capitalize"
              type="button"
              tabindex="-1"
            >{{$t('general.search')}}</button>
          </router-link>
        </div>
      </div>
    </div>

    <div id="mobile-search-container" class="mobile-search-container uk-align-center uk-hidden@m">
      <div class="uk-width-1-1" uk-grid>
        <div class="uk-width-1-1">
          <div
            @mouseover="resolveHover(true)"
            @mouseleave="resolveHover(false)"
            class="search-input-mobile"
            :class="{'input-inactive': !inputActive, 'input-active': inputActive}"
          >
            <form
              v-on:submit.prevent="search"
              novalidate
              class="uk-search uk-width-1-1 uk-height-1-1"
            >
              <img
                v-if="!inputActive"
                :data-src="slateSearchIcon"
                class="icon-dimensions uk-position-center-left"
                uk-img
              />
              <img
                v-else
                :data-src="blueSearchIcon"
                class="icon-dimensions uk-position-center-left"
                uk-img
              />
              <input
                autocomplete="off"
                required
                class="uk-search-input input-search-term uk-position-center-right"
                type="search"
                :placeholder="$t('general.searchForResources')"
                v-model="searchText"
                id="mobile-home-search"
                @focus="resolveFocus(true)"
                @blur="resolveFocus(false)"
              />
            </form>
          </div>
        </div>
      </div>

      <div v-if="!isMobileLogo" class="uk-width-1-1 dropdown-margin-mobile" uk-grid>
        <div class="uk-width-1-2">
          <button
            id="mobile_btn_grades"
            class="uk-float-right uk-text-center uk-button btn-mobile-dropdown"
            type="button"
            tabindex="-1"
          >
            <span v-if="highlightGrades">
              <span class="filter-text-highlight uk-text-capitalize">{{selectedGrades}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-up; ratio: 1.25"></span>
            </span>
            <span v-else>
              <span class="filter-text uk-text-capitalize">{{selectedGrades}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-down; ratio: 1.25"></span>
            </span>
          </button>

          <div
            uk-dropdown="mode: click"
            class="select-filters-container overlay"
            @hidden="highlightGrades = false"
            @show="highlightGrades = true"
          >
            <div>
              <div v-for="filter in filterGroup(GRADES_TITLE)">
                <div
                  class="select-filters"
                  @click="handleFilterUpdate(filters, filter.id, GRADES_TITLE)"
                >
                  <span v-if="!filter.selected" class="uk-text-middle add-pointer">
                    <img :data-src="LRG_UNCHECKED_BOX" class="lrg-checkbox" uk-img />
                  </span>
                  <span v-else class="uk-text-middle add-pointer">
                    <img :data-src="LRG_CHECKED_BOX" class="lrg-checkbox" uk-img />
                  </span>
                  <span v-if="filter.id === ALL_GRADES_ID">
                    <span
                      v-if="filter.id === ALL_GRADES_ID"
                      class="filter-options add-pointer uk-text-bold"
                    >{{filter.title}}</span>
                    <span v-else class="filter-options add-pointer">{{filter.title}}</span>
                    <div class="separator"></div>
                  </span>
                  <span v-else>
                    <span
                      v-if="filter.selected"
                      class="filter-options add-pointer uk-text-bold"
                    >{{filter.title}}</span>
                    <span v-else class="filter-options add-pointer">{{filter.title}}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="uk-width-1-2">
          <button
            id="mobile_btn_subjects"
            class="uk-float-right uk-text-center uk-button btn-mobile-dropdown"
            type="button"
            tabindex="-1"
          >
            <span v-if="highlightSubjects">
              <span class="filter-text-highlight">{{selectedSubjects}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-up; ratio: 1.25"></span>
            </span>
            <span v-else>
              <span class="filter-text">{{selectedSubjects}}</span>
              <span class="dropdown-triangle" uk-icon="icon: triangle-down; ratio: 1.25"></span>
            </span>
          </button>
          <div
            uk-dropdown="mode: click"
            class="select-filters-container overlay"
            @hidden="highlightSubjects = false"
            @show="highlightSubjects = true"
          >
            <div>
              <div v-for="filter in filterGroup(SUBJECTS_TITLE)">
                <div
                  class="select-filters"
                  @click="handleFilterUpdate(filters, filter.id, SUBJECTS_TITLE)"
                >
                  <span v-if="!filter.selected" class="uk-text-middle add-pointer">
                    <img :data-src="LRG_UNCHECKED_BOX" class="lrg-checkbox" uk-img />
                  </span>
                  <span v-else class="uk-text-middle add-pointer">
                    <img :data-src="LRG_CHECKED_BOX" class="lrg-checkbox" uk-img />
                  </span>
                  <span v-if="filter.id === ALL_SUBJECTS_ID || filter.id === SOCIAL_STUDIES_ID">
                    <span
                      v-if="filter.id === ALL_SUBJECTS_ID"
                      class="filter-options add-pointer uk-text-bold"
                    >{{filter.title}}</span>
                    <span v-else class="filter-options add-pointer">{{filter.title}}</span>
                    <div class="separator"></div>
                  </span>
                  <span v-else>
                    <span
                      v-if="filter.selected"
                      class="filter-options add-pointer uk-text-bold"
                    >{{filter.title}}</span>
                    <span v-else class="filter-options add-pointer">{{filter.title}}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <button
          class="uk-button btn-mobile-search uk-text-capitalize"
          type="button"
          tabindex="-1"
          @click="search"
        >{{$t('general.search')}}</button>
      </div>
    </div>
  </div>
</template>


<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import * as grades from '../../enums/grades'
import * as subjects from '../../enums/subjects'
import selectedFilters from '../../utils/selected-filters-util'
import gradeUtil from '../../utils/grade-util'
import * as event from '../../enums/mixpanel-event'
import iconUtil from '../../utils/icon-util'
import * as misc from '../../enums/misc'
import screenUtil from '../../utils/screen-util'
import mixpanel from '../../services/mixpanel'
import * as url from '../../enums/static-urls'

export default {
  name: 'home-search-bar',
  data() {
    return {
      ALL_GRADES_ID: grades.ALL_GRADES_ID,
      ALL_SUBJECTS_ID: subjects.ALL_SUBJECTS_ID,
      SOCIAL_STUDIES_ID: subjects.SOCIAL_STUDIES_ID,
      GRADES_TITLE: grades.GRADES_TITLE,
      SUBJECTS_TITLE: subjects.SUBJECTS_TITLE,
      highlightSubjects: false,
      highlightGrades: false,
      selectedGrades: '',
      selectedSubjects: '',
      LRG_CHECKED_BOX: iconUtil.resolve(misc.LRG_CHECKED),
      LRG_UNCHECKED_BOX: iconUtil.resolve(misc.LRG_UNCHECKED),
      hover: false,
      focus: false,
      slateSearchIcon: iconUtil.resolve(misc.SEARCH_ICON),
      blueSearchIcon: iconUtil.resolve(misc.SEARCH_ICON_HOVER),
      screenWidth: 0,
      isMobileLogo: false
    }
  },
  computed: {
    ...mapGetters(['filters', 'searchTerm']),
    searchText: {
      get: function() {
        return this.searchTerm
      },
      set: function(value) {
        // this.updateSearchText(value)
        this.setSearchCriteria({ searchText: value })
      }
    },
    selectedGradesCount: {
      get: function() {
        return this.filters
          .find(o => o.title === grades.GRADES_TITLE)
          .items.filter(f => f.selected)
      }
    },
    selectedSubjectsCount: {
      get: function() {
        return this.filters
          .find(o => o.title === subjects.SUBJECTS_TITLE)
          .items.filter(f => f.selected)
      }
    },
    gradesFilters: {
      get: function() {
        return this.filters.find(o => o.title === grades.GRADES_TITLE).items
      }
    },
    inputActive: {
      get: function() {
        return this.hover || this.focus
      }
    },
    defaultLang() {
      return this.$i18n.locale === 'en' || this.$i18n.locale === 'en-US'
    }
  },
  watch: {
    selectedGradesCount(selectedGrades) {
      if (selectedGrades.length === 0) {
        this.setDefaultFilterGroup({
          filters: this.filters,
          filterGroup: grades.GRADES_TITLE
        })
      } else if (selectedGrades.length === 1) {
        this.selectedGrades = this.resolveTitle(selectedGrades[0].title)
      } else {
        let level = this.defaultLang
          ? this.$t('general.grades')
          : this.$t('general.ages')
        this.selectedGrades = this.resolveTitle(
          selectedGrades.length + ' ' + level
        )
      }
    },
    selectedSubjectsCount(selectedSubjects) {
      if (selectedSubjects.length === 0) {
        this.setDefaultFilterGroup({
          filters: this.filters,
          filterGroup: subjects.SUBJECTS_TITLE
        })
      } else if (selectedSubjects.length === 1) {
        this.selectedSubjects = this.resolveTitle(selectedSubjects[0].title)
      } else {
        this.selectedSubjects = this.resolveTitle(
          selectedSubjects.length + ' ' + this.$t('general.subjects')
        )
      }
    },
    searchTerm(value) {
      this.searchText = value
    }
  },
  methods: {
    ...mapActions([
      'performSearch',
      'updateSelectedFilters',
      'setDefaultFilterGroup',
      'setSearchCriteria',
      'cleanSetSearchCriteria',
      'setDefaultFilters'
    ]),
    ...mapMutations(['updateFilters', 'updateSearchText']),
    resolveWindowWidth() {
      this.screenWidth = screenUtil.resolveWidth()
      this.isMobileLogo = this.screenWidth < 1024
    },
    search() {
      mixpanel.track(event.SEARCH_RESOURCES, {
        searchTerm: this.searchTerm,
        grades: this.resolveGrades(),
        subjects: this.resolveSubjects()
      })
      this.cleanSetSearchCriteria(this.searchText)
      this.performSearch({ searchText: this.searchText })
    },
    resolveTitle(title) {
      let strLength = 12
      if (this.$i18n.locale === 'ru') {
        strLength = 11
      }
      if (title.length > strLength) {
        return title.substring(0, 9) + '...'
      } else {
        return title
      }
    },
    resolveHover(hover) {
      this.hover = hover
    },
    resolveFocus(focus) {
      this.focus = focus
    },
    filterGroup(group) {
      return this.filters.find(o => o.title === group).items
    },
    handleFilterUpdate(filters, filterId, filterGroup) {
      if (
        filterId === this.ALL_GRADES_ID ||
        filterId === this.ALL_SUBJECTS_ID
      ) {
        this.setDefaultFilterGroup({
          filters: filters,
          filterGroup: filterGroup
        })
      } else {
        this.updateSelectedFilters({
          filters: filters,
          filterId: filterId,
          filterGroup: filterGroup
        })
      }
    },
    isSearchBarVisible() {
      try {
        let rect = document
          .getElementById('search-container')
          .getBoundingClientRect()
        let isVisible = null
        if (rect.top !== 0) {
          let elemTop = rect.top
          let elemBottom = rect.bottom
          // Only completely visible elements return true
          isVisible = elemTop >= 0 && elemBottom <= window.innerHeight
        } else {
          let mobileRect = document
            .getElementById('mobile-search-container')
            .getBoundingClientRect()
          let elemTop = mobileRect.top
          let elemBottom = mobileRect.bottom
          isVisible = elemTop >= 0 && elemBottom <= window.innerHeight
        }
        this.$emit('searchBarVisible', isVisible)
      } catch (err) {}
    },
    resolveGrades() {
      let selectedGrades = ''
      try {
        let gradeFilters = this.resolveFilter(grades.GRADES_TITLE)
        for (let grade of gradeFilters) {
          selectedGrades = selectedGrades + ' ' + gradeUtil.resolve(grade)
        }
        return selectedGrades
      } catch (err) {
        return null
      }
    },
    resolveSubjects() {
      let selectedSubjects = ''
      try {
        let subjectFilters = this.resolveFilter(subjects.SUBJECTS_TITLE)
        for (let subject of subjectFilters) {
          selectedSubjects = selectedSubjects + ' ' + subject
        }
        return selectedSubjects
      } catch (err) {
        return null
      }
    },
    resolveFilter(filterGroup) {
      let filters = selectedFilters.resolve(this.filters)
      if (
        filters.find(o => o.filterGroup === filterGroup).selectedFilters.length
      ) {
        return filters.find(o => o.filterGroup === filterGroup).selectedFilters
      } else {
        return null
      }
    },
    openUrl() {
      window.open(url.RESOURCE_SUBMISSION_TERMS, '_blank')
    }
  },
  created() {
    window.addEventListener('scroll', this.isSearchBarVisible)
  },
  mounted() {
    this.setDefaultFilters(this.filters)
    this.isSearchBarVisible()
    this.resolveWindowWidth()
    window.addEventListener('resize', this.resolveWindowWidth)
  },
  destroyed() {
    window.removeEventListener('resize', this.resolveWindowWidth)
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/breakpoints.scss';
@import '../../styles/colours.scss';

.btn-dropdown {
  cursor: pointer;
  margin-top: 28px;
  margin-left: -5px;
  border: none;
  background-color: transparent;
}

.btn-mobile-dropdown {
  cursor: pointer;
  margin-left: -5px;
  border: none;
  background-color: transparent;
}

.btn-mobile-search {
  width: 416px;
  height: 44px;
  font-size: 20px;
  margin-top: 20px;
  margin-left: 30px;
  background-color: $light-blue;
  color: white;
  border-radius: 2px;

  &:hover {
    background-color: white;
    color: $light-blue;
    box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
  }
}

.btn-search {
  width: 127px;
  height: 42px;
  font-size: 20px;
  padding-left: 0;
  padding-right: 0;
  margin-top: 28px;
  background-color: $light-blue;
  color: white;
  border-radius: 2px;

  &:hover {
    background-color: white;
    color: $light-blue;
    box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
  }
}

.dropdown-margin-mobile {
  margin-top: 0;
}

.dropdown-triangle {
  color: $light-blue;
  cursor: pointer;
}

.filter-options {
  font-size: 15px;
}

.filter-text {
  font-size: 20px;
  color: $content-text;

  &:hover {
    color: $light-blue;
  }
}

.filter-text-highlight {
  font-size: 20px;
  color: $light-blue;
}

h1 {
  color: white;
  font-size: 48px;
  font-weight: bold;
  margin-top: 43px;
  margin-bottom: 0;
}

.heading-container {
  max-width: 1110px;
}

h2 {
  color: white;
  font-size: 26px;
  margin-top: 0;
}

.hero-image {
  height: 359px;
  background-image: url('../../../static/custom-icons/SMEX_hero_banner_1920X464-final.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
}

.icon-dimensions {
  margin-left: 9px;
  height: 40px;
  width: 40px;
}

.input-active {
  border: solid 1px $light-blue;
  background: white;
  box-shadow: 2px 4px 8px 0 rgba(0, 0, 0, 0.24);
}

.input-inactive {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.24);
}

.input-search-term {
  height: 100%;
  width: 88%;
}

/* required for IE*/
.input-search-term::-ms-clear {
  display: none;
}

.lrg-checkbox {
  height: 29px;
  width: 29px;
}

.mobile-search-container {
  width: 472px;
  height: 220px;
  border-radius: 2px;
  margin-top: 30px;
  background-color: #e3e5e8;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.24);
}

.search-container {
  width: 1110px;
  height: 93px;
  border-radius: 2px;
  margin-top: 24px;
  background-color: #e3e5e8;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.24);
}

.search-input {
  width: 499px;
  height: 56px;
  border-radius: 2px;
  background: white;
  margin-left: 32px;
  margin-top: 18px;
  margin-bottom: 19px;
}

.search-input-mobile {
  width: 416px;
  height: 56px;
  border-radius: 2px;
  background: white;
  margin-left: 30px;
  margin-top: 18px;
  margin-bottom: 19px;
}

.uk-button {
  text-transform: none;
}

.uk-dropdown {
  padding: 0;
}

@media screen and (max-width: $breakpoint-large) {
  .search-container {
    width: 888px;
    height: 74.4px;
    border-radius: 1.6px;
  }
  .heading-container {
    max-width: 888px;
  }

  .search-input {
    width: 399.2px;
    height: 44.8px;
    border-radius: 1.6px;
    margin-top: 14px;
  }
  .btn-dropdown {
    padding-left: 15px;
    padding-right: 15px;
    margin-top: 18px;
  }
  .btn-search {
    width: 101px;
    height: 33px;
    border-radius: 1.6px;
    margin-top: 20px;
    font-size: 16px;
    line-height: 20px;
    padding-left: 0;
    padding-right: 0;
  }
  .search-icon {
    padding-bottom: 5px;
  }
  .filter-text {
    font-size: 16px;
  }
  .filter-text-highlight {
    font-size: 16px;
  }
  h1 {
    font-size: 38.4px;
    margin-top: 43px;
    margin-bottom: 0;
  }
  h2 {
    margin-top: 0;
    font-size: 20.8px;
  }
  .hero-image {
    height: 316px;
  }
}

@media screen and (max-width: $breakpoint-medium) {
  h1 {
    font-size: 28px;
    margin-top: 23px;
    margin-bottom: 0;
  }
  h2 {
    font-size: 18px;
    padding: 0px 30px;
    margin-top: 0;
  }
  .heading-container {
    max-width: 680px;
  }
  .hero-image {
    height: 410px;
  }
  .mobile-search-container {
    height: 163px;
  }
  .btn-mobile-search {
    margin-top: 17px;
  }
  .search-input-mobile {
    margin-bottom: 0px;
  }
}

@media screen and (max-width: $breakpoint-small) {
  h1 {
    text-align: left !important;
    font-size: 24px;
    margin-top: 23px;
    margin-bottom: 15px;
  }
  h2 {
    text-align: left !important;
    font-size: 18px;
    padding: 0;
    margin-top: 0;
  }
  .heading-container {
    max-width: 321px;
    margin-left: 30px;
  }
  .hero-image {
    height: 385px;
  }
  .search-input-mobile {
    width: 90%;
  }
  .mobile-search-container {
    width: 90%;
  }
  .input-search-term {
    width: 83%;
  }
  .btn-mobile-search {
    width: 83%;
  }
  .uk-button {
    padding: 0;
  }
}
</style>



